import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Activity, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialMode = params.get('mode');
  const couponParam = params.get('coupon');
  const [isLogin, setIsLogin] = useState(initialMode !== 'register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [phone, setPhone] = useState('');
  const [mciNumber, setMciNumber] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        if (couponParam) navigate(`/subscription?coupon=${encodeURIComponent(couponParam)}`);
        else navigate('/dashboard');
      } else {
        await register({ name, email, password, clinic_name: clinicName, phone, mci_registration_number: mciNumber });
        if (couponParam) navigate(`/subscription?coupon=${encodeURIComponent(couponParam)}`);
        else navigate('/new-case');
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/4225925/pexels-photo-4225925.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Doctor examining X-ray"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A4D2E]/80 to-[#1A4D2E]/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-6">
            <Activity className="w-8 h-8" />
            <span className="text-2xl font-heading font-semibold tracking-tight">ChestBuddy</span>
          </div>
          <h1 className="text-4xl font-heading font-medium tracking-tight mb-4 leading-tight">
            AI-Powered Chest X-Ray<br />Analysis for GPs
          </h1>
          <p className="text-white/80 text-lg max-w-md leading-relaxed">
            ChestAI GP-Assist — Clinically credible decision support. Upload, analyze, and generate reports from your clinic.
          </p>
        </div>
      </div>

      {/* Right auth form */}
      <div className="flex-1 flex items-center justify-center bg-stone-50 p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Activity className="w-7 h-7 text-[#1A4D2E]" />
            <span className="text-xl font-heading font-semibold text-stone-900">ChestBuddy</span>
          </div>

          <Card className="border-stone-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-heading text-stone-900">
                {isLogin ? 'Welcome back' : 'Create account'}
              </CardTitle>
              <CardDescription className="text-stone-500">
                {isLogin ? 'Sign in to access your dashboard' : 'Register to start analyzing X-rays'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <>
                    <div>
                      <Label htmlFor="name" className="text-stone-700">Full Name</Label>
                      <Input data-testid="register-name-input" id="name" value={name} onChange={e => setName(e.target.value)} placeholder="Dr. John Smith" required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="clinic" className="text-stone-700">Clinic Name</Label>
                      <Input data-testid="register-clinic-input" id="clinic" value={clinicName} onChange={e => setClinicName(e.target.value)} placeholder="City Health Clinic" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-stone-700">Phone</Label>
                      <Input data-testid="register-phone-input" id="phone" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 9876543210" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="mci" className="text-stone-700">MCI/NMC Registration No.</Label>
                      <Input data-testid="register-mci-input" id="mci" value={mciNumber} onChange={e => setMciNumber(e.target.value)} placeholder="MCI-12345" className="mt-1 font-mono" />
                    </div>
                  </>
                )}
                <div>
                  <Label htmlFor="email" className="text-stone-700">Email</Label>
                  <Input data-testid="login-email-input" id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="doctor@clinic.com" required className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-stone-700">Password</Label>
                  <div className="relative mt-1">
                    <Input data-testid="login-password-input" id="password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" required minLength={6} />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {error && <div data-testid="auth-error" className="text-sm text-[#8B2626] bg-red-50 p-3 rounded-lg">{error}</div>}

                <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-[#1A4D2E] hover:bg-[#143B23] text-white rounded-lg h-11 font-medium">
                  {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  data-testid="toggle-auth-mode"
                  onClick={() => { setIsLogin(!isLogin); setError(''); }}
                  className="text-sm text-[#1A4D2E] hover:underline font-medium"
                >
                  {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign in'}
                </button>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-stone-400 text-center mt-6 max-w-xs mx-auto leading-relaxed">
            ChestBuddy: ChestAI GP-Assist — AI decision support system. Clinical judgment is required.
          </p>
          <div className="text-center mt-3">
            <a href="/admin/login" data-testid="admin-login-link" className="text-xs text-stone-400 hover:text-[#1A4D2E] transition-colors">
              Admin Login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
